import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';
import { StandardEvents } from '@shopify/events';

class MolyVitaPackSelector extends Component {
  #controller;
  #config;
  #selection = 0;

  connectedCallback() {
    super.connectedCallback();
    this.#controller?.abort();
    this.#controller = new AbortController();
    const signal = this.#controller.signal;
    const owner = this.closest('.shopify-section');
    if (!owner) return;
    owner.addEventListener(ThemeEvents.quantitySelectorUpdate, () => this.#sync(), { signal });
    owner.addEventListener(StandardEvents.productSelect, async (event) => {
      const selection = ++this.#selection;
      try {
        if (event.promise) await event.promise;
        if (!signal.aborted && selection === this.#selection) this.#sync();
      } catch {
        if (!signal.aborted) this.#sync();
      }
    }, { signal });
    customElements.whenDefined('quantity-selector-component').then(() => {
      if (!signal.aborted) this.#sync();
    });
  }

  updatedCallback() {
    super.updatedCallback();
    this.#config = undefined;
    this.#sync();
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#selection++;
    super.disconnectedCallback();
  }

  #state() {
    const owner = this.closest('.shopify-section');
    const form = owner?.querySelector('product-form-component');
    const native = form?.querySelector('quantity-selector-component');
    const idInput = form?.querySelector('input[name="id"]');
    if (!(idInput instanceof HTMLInputElement) || typeof native?.getCurrentValues !== 'function') return;
    if (!this.#config) {
      try { this.#config = JSON.parse(this.querySelector('[data-pack-data]')?.textContent || '{}'); }
      catch { return; }
    }
    const variant = this.#config.variants?.find(item => String(item.id) === idInput.value);
    if (!variant) return;
    return { native, variant, values: native.getCurrentValues() };
  }

  #valid(quantity, state) {
    const { min, max, step, cartQuantity } = state.values;
    return state.variant.available && quantity >= min && (quantity - min) % step === 0 && (max === null || quantity + cartQuantity <= max);
  }

  #unitPrice(variant, quantity) {
    let price = variant.price;
    for (const entry of variant.breaks || []) if (quantity >= entry.min) price = entry.price;
    return price;
  }

  #money(cents) {
    try { return new Intl.NumberFormat(this.#config.locale, { style: 'currency', currency: this.#config.currency, currencyDisplay: 'code' }).format(cents / 100); }
    catch { return `${this.#config.currency} ${(cents / 100).toFixed(2)}`; }
  }

  #sync() {
    const state = this.#state();
    const controls = this.querySelector('[data-pack-controls]');
    if (!state || !controls) return;
    controls.hidden = false;
    const quantities = [1, 2, 3].filter(quantity => this.#valid(quantity, state));
    const minimum = Math.min(...quantities.map(quantity => this.#unitPrice(state.variant, quantity)));
    const first = this.#unitPrice(state.variant, 1);
    for (const button of this.querySelectorAll('button[data-quantity]')) {
      const quantity = Number(button.dataset.quantity);
      button.disabled = !this.#valid(quantity, state);
      button.setAttribute('aria-pressed', String(quantity === state.values.value));
      button.setAttribute('aria-label', this.#config.optionLabel.replace('__COUNT__', String(quantity)).replace('__OPTION__', state.variant.title));
      const total = this.#money(this.#unitPrice(state.variant, quantity) * quantity);
      const price = button.querySelector('[data-pack-price]');
      if (price && price.textContent !== total) price.textContent = total;
      const badge = button.querySelector('[data-pack-badge]');
      if (badge) badge.hidden = button.disabled || minimum >= first || this.#unitPrice(state.variant, quantity) !== minimum;
    }
    const summary = this.querySelector('[data-pack-summary]');
    const total = this.#money(this.#unitPrice(state.variant, state.values.value) * state.values.value);
    const value = this.#config.summary.replace('__TOTAL__', total);
    if (summary && summary.textContent !== value) summary.textContent = value;
  }

  selectPack(event) {
    if (!(event.target instanceof Element)) return;
    const button = event.target.closest('button[data-quantity]');
    if (!(button instanceof HTMLButtonElement) || !this.contains(button) || button.disabled) return;
    const state = this.#state();
    const quantity = Number(button.dataset.quantity);
    if (!state || !this.#valid(quantity, state)) return;
    state.native.setValue(String(quantity));
    state.native.onQuantityChange();
    state.native.updateButtonStates();
    this.#sync();
  }
}

if (!customElements.get('mv-pack-selector')) customElements.define('mv-pack-selector', MolyVitaPackSelector);
